import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  TextField,
  IconButton,
  Popover,
  Box,
  Grid,
  Button,
  Tooltip,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { AddCircleRounded, RemoveCircleOutline } from "@mui/icons-material";
import { renderAlert } from "../../../../CommonComponents/Alert/Alert.jsx";
import { checkForDuplicates } from "../../helper/helpersSubModule.js";
import SelectColorBox from "./SelectColorBox.jsx";
import { isStringifiedArrayWithPropName } from "../../../../helpers/ExportHelper.js";
import { ignoredKeys, timeSeriesChildColumnListData } from "./constants/MdvConstant.js";
import { isArrayModified, logicToUpdateExistingData, validatePropAbbreviation, validatePropName, validatePropValue } from "../../helper/MdvHelper.ts";
import HandleMultiDimensionalInputBox from "./HandleMultiDimensionInputBox.jsx";
import { processDate } from "../../helper/dateHelper.js";
import dayjs from 'dayjs';
import { extendValuesToWholeRow, handleAddValueHelper, processListDataHelper } from "../../helper/helperFunctions.js";
import ListStructure from "./sub-components/ListStructure.jsx";
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid';
import { columnDefs } from "../../../../helpers/helper.js";
import { isRollupApplicable } from "../../Menus/helpers/columnUpdateMenuHelper.js";


const MultiDimensionalValueTypeEditor = forwardRef((props, ref) => {
  const { params, updateListData, updateDB, onClose, activeEditorId, index, gridRef } = props;
  
  const [data, setData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [addNewField, setAddNewField] = useState(false);
  const [removeField, setRemoveField] = useState(false);
  const [valuestoBeExtended,setValuesToBeExtended] = useState([])
  const [listValue, setListValue] = useState([]);
  const [fields, setFields] = useState([
    {
      propName: "",
      propType: "text",
      propValue: "",
      propAbbrevation: "",
      propBgColor: "none",
      propFgColor: "none",
      propList:[],
      propId: uuidv4(),
    },
  ]);
  const popoverRef = useRef(null);
  const textFieldRefs = useRef({});
  const saveButtonRef = useRef(null);
  const inputRefs = useRef([]);
  const bgColorRefs = useRef([]);
  const fgColorRefs = useRef([]);
  const addCircleButtonRefs = useRef([]);
  const removeButtonRefs = useRef([]);
  const okButtonRefs = useRef([]);
  const cancelButtonRefs = useRef([]);
  const propListRefs = useRef([]);
  const [existingData, setExistingData] = useState([])
  const [originalExistingData, setOriginalExistingData] = useState([])
  const [isModify, setIsModify] = useState(false)
  const [isTimeSeries, setisTimeseries] = useState(false)
  const [reasonField,setReasonField]= useState(false)

  useEffect(() => {
    if(params && params.colDef && params.colDef.type === "multi dimensional value" && !params.colDef.block){
      setisTimeseries(false)
    }else{
      setisTimeseries(true)
    }
  }, [params]);

  useEffect(() => {
    processListData()
  }, [params?.colDef?.listData, params?.value]);

  useEffect(() => {
    if (addNewField && fields.length > 0) {
      inputRefs.current[(fields.length - 1) * 7]?.focus()
      setAddNewField(false)
    }
  }, [addNewField]);

  useEffect(() => {
    if (removeField && fields.length > 0) {
      inputRefs.current[(fields.length - 1)/3 * 7]?.focus()
      setRemoveField(false)
    }
  }, [removeField]);

  useEffect(() => {
    if (props.isEditRowPopup || !popoverRef.current) return;
    // AG-Grid positions the popup wrapper before the async data renders, so once
    // the content has grown we shift the wrapper back inside the viewport.
    const timer = setTimeout(repositionPopupWithinViewport, 0);
    return () => clearTimeout(timer);
  }, [data])

  const repositionPopupWithinViewport = () => {
    const boxEl = popoverRef.current;
    if (!boxEl) return;
    // The element AG-Grid positioned (inline top/left relative to the popup parent)
    let wrapper = boxEl.parentElement;
    while (wrapper && !wrapper.classList.contains("ag-popup-child")) {
      wrapper = wrapper.parentElement;
    }
    if (!wrapper) return;
    const rect = boxEl.getBoundingClientRect();
    // Keep the popup inside the grid's visible box — the viewport bottom is covered
    // by the horizontal scrollbar / app footer, so window bounds alone are not enough
    const gridRect = params.eGridCell?.closest?.(".ag-root-wrapper")?.getBoundingClientRect();
    const boundaryBottom = Math.min(window.innerHeight, gridRect?.bottom ?? Infinity);
    const boundaryRight = Math.min(window.innerWidth, gridRect?.right ?? Infinity);
    // overflow:visible lets content spill past the Box's maxHeight, so measure scrollHeight too
    const visualBottom = rect.top + Math.max(rect.height, boxEl.scrollHeight);
    const overflowY = visualBottom - boundaryBottom;
    const overflowX = rect.right - boundaryRight;
    if (overflowY > 0) {
      wrapper.style.top = `${Math.max(0, parseFloat(wrapper.style.top) - overflowY)}px`;
    }
    if (overflowX > 0) {
      wrapper.style.left = `${Math.max(0, parseFloat(wrapper.style.left) - overflowX)}px`;
    }
  };

  const processListData = (isCancel) => {
    processListDataHelper({
      params,
      isCancel,
      isStringifiedArrayWithPropName,
      setFields,
      setData,
      setReasonField
    });
  };
  
  

  useImperativeHandle(ref, () => ({
    getValue: () => data,
  }));

  const handleInputChange = async (index, event, propFieldName, propFieldValue) => {
    const { name, value } = event.target;
    const fieldName = name || propFieldName; // Use a new variable
    const fieldValue = fieldName === 'propList' ? propFieldValue : value
    
    if (fieldName === "propAbbrevation" && fieldValue.length > 3) {
      renderAlert("Abbreviation must be 3 letters", "error", 2000);
      return;
    }
  
    const values = [...fields];
    if(fieldName === 'propType' && fieldValue !== 'multi-list'){
      delete values[index]?.propList
      delete values[index]?.propValue
    }



    values[index][fieldName] = fieldValue; // Use the new variable

    if(fieldName === 'rollup' && fieldValue==='None'){
      delete values[index][fieldName]
    }

    if(fieldName === 'propType'){
      const nonRollupFields = ['text','boolean','multi-list','single-list']
      if(nonRollupFields.includes(fieldValue)){
        if(values[index][fieldName]){
          delete values[index]['rollup']
        }else{
          values[index]['rollup'] = 'None';
        }
      }
    }
    const shouldModify = await isArrayModified(data, values)
    setFields(values);
    setIsModify(shouldModify)
  };
  
  const handleAddField = () => {
    const newField = {
      propName: "",
      propType: "text",
      propValue: "",
      propAbbrevation: "",
      propId: uuidv4()
    };
    const duplicates = checkForDuplicates(fields);

    if (duplicates.propNames.size > 0) {
      renderAlert("Property name already exists", "error", 2000);
    } else if (duplicates.propAbbrevation.size > 0) {
      renderAlert("Property abbreviation already exists", "error", 2000);
    } else {
      setFields([...fields, newField]);
      setAddNewField(true)
    }
  };

  const handleRemoveField = (index) => {
    const values = [...fields];
    values.splice(index, 1);
    setFields(values);
    setRemoveField(true)
    setIsModify(true)
  };

  const handlePropValueChange = (index, newValue, type, propName) => {
    if (!validatePropValue(newValue, type, params?.colDef, propName)) {
      return;
    }
    
    const newData = JSON.parse(JSON.stringify(data));
    newData[index].propValue = newValue;
    if (params?.colDef?.type === "multi dimensional value" && params?.colDef?.block) {
      // Clear Reason when Actual Time < Planned Time
      const plannedObj = newData.find(item => item.propName === "Planned Time");
      const actualObj = newData.find(item => item.propName === "Actual Time");
      const reasonObjIndex = newData.findIndex(item => item.propName === "Reason");

      if (plannedObj && actualObj && reasonObjIndex !== -1) {
        const planned = parseFloat(plannedObj.propValue || 0);
        const actual = parseFloat(actualObj.propValue || 0);

        // If Actual < Planned → clear Reason
        if (actual < planned) {
          newData[reasonObjIndex].propValue = "";
        }
      }
    }

    setData(newData);
  };

  const checkBoxClicked =(index,copyTimeTillEndDate)=>{
    //if checkbox is checked
    if(copyTimeTillEndDate) {
      setValuesToBeExtended([...valuestoBeExtended,index])
    }
    else {
      let newListForFieldsToBeExtended = valuestoBeExtended.filter((name)=>name!==index)
      setValuesToBeExtended(newListForFieldsToBeExtended)
    }
  }
//
  const handlePopoverOpen = async (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setExistingData([])
    setOriginalExistingData([])
    const {newRows, originalRows} = await logicToUpdateExistingData(gridRef, columnDefs, params)
    setExistingData(newRows)
    setOriginalExistingData(originalRows)
  };

  const handlePopoverClose = (event) => {
    event.stopPropagation();
    params.api.setFocusedCell(params.node.rowIndex, params.column);
    setAnchorEl(null);
    processListData(true);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && event.target.name !== "propList") {
      handleAddValue(event, "enter");
    }
  };

  const getSequencedIndex = () => {
    return Object.keys(textFieldRefs.current)?.sort();
  }

  const handleTabPress = (index, event, type) => {
    event.preventDefault();
    event.stopPropagation()
    if (event.key === "Tab") {
      const _sequencedIndex = getSequencedIndex();

      if(event.shiftKey) {

        const isFirstKey = _sequencedIndex?.indexOf(index) === 0;

        if(isFirstKey) {
          saveButtonRef.current.focus();
        } else {
          const prevIndex = _sequencedIndex[_sequencedIndex?.indexOf(index) - 1];
          textFieldRefs.current[prevIndex].focus();
        }
      } else {
        const isLastField = _sequencedIndex?.indexOf(index) === (_sequencedIndex?.length - 1);
  
        if(isLastField) {
          saveButtonRef.current.focus();
        } else {
          const nextIndex = _sequencedIndex[_sequencedIndex?.indexOf(index) + 1];
          textFieldRefs.current[nextIndex].focus();
        }
      }
    }
    if(type && type === 'number'){
      if (ignoredKeys.has(event.key)) {
        return;
      }
      if (!/[0-9]/.test(event.key) && event.key !== '.' || (event.key === '.' && event.target.value.includes('.'))) {
        renderAlert('Please insert valid numbers', 'warning', 2000);
      }
      
    }else{
      return;
    }
  };
     
  const handleKeyDownOnPropList = (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    
    if(event.key === 'Tab'){
      if (index === fields.length - 1) {
        okButtonRefs.current.focus()
      }else{
        handleKeyDown(event, index, 7)
      }  
    }
  }

  const handleAddCircleButtonKeyDown = (event) => {
    event.preventDefault()
    if(fields[fields.length-1].propType === 'multi-list' || fields[fields.length-1].propType === 'single-list'){
      propListRefs.current[propListRefs.current.length-1].focus()
    }else{
      okButtonRefs.current.focus()
    }
  }

  const handleKeyDown = (event, index, subIndex) => {
    if (event.key !== "Tab") return;
  
    event.preventDefault();
  
    const totalFields = fields.length;
    const totalSubFields = 7; // [0-6]: propName, abbrev, type, rollup, bg, fg, remove
    const currentField = fields[index];
    const hasList = currentField.propType === "single-list" || currentField.propType === "multi-list";
  
    let nextIndex = index;
    let nextSubIndex = subIndex;
  
    if (event.shiftKey) {
      nextSubIndex--;
      if (nextSubIndex < 0) {
        if (index === 0) {
          props.prevRef.current?.focus();
          return;
        }
        nextIndex = index - 1;
        nextSubIndex = 6;
        if (
          fields[nextIndex].propType === "single-list" ||
          fields[nextIndex].propType === "multi-list"
        ) {
          nextSubIndex = 7;
        }
      }
    } else {
      nextSubIndex++;
      if (nextSubIndex === 3 && !isRollupApplicable(currentField.propType)) {
        nextSubIndex = 4;
      }
  
      if (nextSubIndex > 6 && hasList) {
        nextSubIndex = 7;
      } else if (nextSubIndex > 6 && !hasList) {
        // Move to next field row
        nextIndex = index + 1;
        nextSubIndex = 0;
  
        if (nextIndex >= totalFields) {
          props.okButtonRefs?.current?.focus?.();
          return;
        }
      } else if (nextSubIndex > 7) {
        // Going beyond list
        nextIndex = index + 1;
        nextSubIndex = 0;
  
        if (nextIndex >= totalFields) {
          props.okButtonRefs?.current?.focus?.();
          return;
        }
      }
    }
  
    const nextRefIndex = nextIndex * totalSubFields + nextSubIndex;
    const nextField = fields[nextIndex];
  
    // Focus the correct element
    switch (nextSubIndex) {
      case 0:
      case 1:
      case 2:
      case 3:
        inputRefs.current[nextRefIndex]?.focus();
        break;
      case 4:
        bgColorRefs.current[nextRefIndex]?.firstRef?.focus();
        break;
      case 5:
        fgColorRefs.current[nextRefIndex]?.firstRef?.focus();
        break;
      case 6:
        removeButtonRefs.current[nextRefIndex]?.focus();
        break;
      case 7:
        propListRefs.current[nextRefIndex]?.focus();
        break;
      default:
        props.okButtonRefs?.current?.focus();
    }
  };
  
  
  
    const handleSaveButtonKeyPress = (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      const _sequencedIndex = getSequencedIndex();
      textFieldRefs.current[event.shiftKey ? _sequencedIndex[_sequencedIndex?.length -1]: _sequencedIndex[0]].focus();
    }
  };
  
  const handleCancelButtonKeyDown = (event) => {
     event.preventDefault()
     inputRefs.current[0].focus()
  }

  const handleOkButtonKeyDown = (event) => {
     event.preventDefault()
     cancelButtonRefs.current.focus()

  }

  const handleAddValue = (event, key) =>
    handleAddValueHelper({
      event,
      key,
      fields,
      params,
      gridRef,
      existingData,
      originalExistingData,
      updateListData,
      updateDB,
      extendValuesToWholeRow,
      checkForDuplicates,
      validatePropAbbreviation,
      validatePropName,
      renderAlert,
      processDate,
      dayjs,
      setReasonField,
      isModify,
      data,
      reasonField,
      props,
      onClose,
      valuestoBeExtended,
    });
  
  return (
  <Box
      ref={popoverRef}
      sx={{
        width: !props.isEditRowPopup ? "403px" : "100%",
        border: !props.isEditRowPopup? "2px solid black":'unset',
        zIndex: 1000,
        position: "relative",
        backgroundColor: "white",
        maxHeight: "400px",
        scrollBehavior: 'smooth',
        overflow: "visible",
        borderRadius: "6px",
      }}
      onKeyDown={handleKeyPress}
    
    >
      <Box>
        <Box
          sx={{
            background: "#134157",
            color: "white",
            fontSize: "12px",
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            scrollBehavior: 'smooth',
            padding: "1rem",
          }}
        >
       {props.isEditRowPopup?"Edit Property Details": `Add Property Details to ${params?.colDef?.headerName}: ${params?.data?.['#']}`}
          <span
            style={{ cursor: "pointer" }}
            onClick={() => {
              if (!props.isEditRowPopup) params.stopEditing()
              else onClose()
            }}
          >
            X
          </span>
        </Box>
        <Box
          className="grid-container"
          sx={{ maxHeight: "450px", overflowY: "auto", paddingTop: "6px",padding: !props.isEditRowPopup? '6px 0px 0px 0px':'6px' }}
        >
               {
                 data.map((item, index) => {
                  const plannedTime = Number(data.find(d => d.propName === "Planned Time")?.propValue || 0);
                  const actualTime  = Number(data.find(d => d.propName === "Actual Time")?.propValue || 0);
                  
                  const allowReason = actualTime > plannedTime;
                   if (item.propName === "Reason" && !allowReason) return null;
                   return (
                     <Grid
                       container
                       spacing={2}
                       alignItems="center"
                       key={index}
                       sx={{ mb: 1, textAlign: "left", padding: "3px" }}
                     >
                       <Grid item xs={4} style={{ paddingTop: "12px" }}>
                         <Tooltip title={item.propName}>
                           <span style={{ fontWeight: "bold" }}>
                             {item.propName.length > 10 && !params?.column?.colDef?.block ?
                               `${item.propName.substring(0, 10)}...`
                               : item.propName}
                           </span>
                           <span>{` [${item.propAbbrevation?.toUpperCase()}] :`}</span>
                         </Tooltip>
                       </Grid>
                       <Grid item xs={8} style={{ paddingTop: "12px", scrollBehavior: 'smooth' }}>
                         <HandleMultiDimensionalInputBox
                           item={item}
                           index={index}
                           handlePropValueChange={handlePropValueChange}
                           handleTabPress={(e, idx) => {
                            if (item.propName === "Reason" && !item.propValue) {
                              // Prevent Reason field from stealing focus if it's empty
                              return;
                            }
                            handleTabPress(e, idx);
                          }}
                           textFieldRefs={textFieldRefs}
                           inputRef={(el) => (textFieldRefs.current[`main_${index}`] = el)}
                           timeSeries={params?.column?.colDef?.block || params?.colDef?.block}
                           checkBoxClicked={checkBoxClicked}
                           showCheckBox={!props.isEditRowPopup}
                           handleKeyPress={(e, idx) => {
                            if (item.propName === "Reason" && !item.propValue) {
                              // Same check for Enter or other key handling
                              return;
                            }
                            handleKeyPress(e, idx);
                          }}
                           params={params}
                           data={data}
                         />
                       </Grid>
                     </Grid>
          )})}
        </Box>
      </Box>
      <Box
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {!props.isEditRowPopup && (
              <IconButton onClick={handlePopoverOpen}>
                <Tooltip title="Add more property">
                  <AddCircleIcon color="primary" />
                </Tooltip>
              </IconButton>
            )}
            <Button
              variant="contained"
              onClick={(e) => handleAddValue(e, "save")}
              style={{ fontSize: "12px", height: "24px", margin: !params?.column?.colDef?.block && !props.isEditRowPopup ? "8px" : props.isEditRowPopup ? "8px 16px" : "8px 4px" }}
              ref={saveButtonRef}
              onKeyDown={handleSaveButtonKeyPress}
            >
              Save
            </Button>
          </Box>
      <Popover
        disablePortal
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            width: 520,
            maxHeight: 350,
            overflowY: "auto",
            borderRadius: 2,
            boxShadow: 4,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "300px",
          }}
        >
          <Box
            sx={{
              background: "#134157",
              color: "white",
              fontSize: "12px",
              fontWeight: "bold",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "1rem",
            }}
          >
            Add new property to {params?.colDef?.headerName}
            <span style={{ cursor: "pointer" }} onClick={handlePopoverClose}>
              X
            </span>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "520px",
              maxHeight: "350px",
              overflowY: "auto",
              height : fields.length < 4 ? fields.slice(0, 4).some(field => 
                field.propType === "multi-list" || field.propType === "single-list"
              ) ? `${fields.length * 150}px` : `${fields.length * 70}px` : '340px'
            }}
          >
            {fields.map((field, index) => (
              <Grid
                container
                spacing={1}
                alignItems="center"
                key={index}
                sx={{ paddingLeft: "2rem", fontSize: "11px", mt: 1, ml: -2 }}
              >
                <Grid item xs={isTimeSeries ? 3 : 2}>
                  <TextField
                    placeholder="Name"
                    sx={{
                      ".MuiOutlinedInput-root": {
                        borderRadius: "20px",
                        height: "34px",
                        fontSize: "12px",
                      },
                      ".MuiInputLabel-root": {
                        fontSize: "12px",
                        transform: "translate(14px, 10px) scale(1)", // Adjust label position for placeholder
                      },
                      ".MuiInputLabel-shrink": {
                        transform: "translate(14px, -6px) scale(0.75)", // Adjust label position when focused
                      },
                    }}
                    fullWidth
                    variant="outlined"
                    name="propName"
                    label="Property Name"
                    disabled={timeSeriesChildColumnListData.some(
                      (item) =>
                        item.propName === field.propName && item.propAbbrevation === field.propAbbrevation
                    ) ||params.colDef.template}
                    value={field.propName}
                    autoFocus={index === 0}
                    onChange={(event) => handleInputChange(index, event)}
                    onKeyDown={(event) => handleKeyDown(event, index, 0)}
                    inputRef={(el) =>
                      (inputRefs.current[index * 7 + 0] = el)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    placeholder="Alias"
                    sx={{
                      ".MuiOutlinedInput-root": {
                        borderRadius: "20px",
                        height: "34px",
                        fontSize: "12px",
                      },
                      ".MuiInputLabel-root": {
                        fontSize: "12px",
                        transform: "translate(14px, 10px) scale(1)", // Adjust label position for placeholder
                      },
                      ".MuiInputLabel-shrink": {
                        transform: "translate(14px, -6px) scale(0.75)", // Adjust label position when focused
                      },
                    }}
                    fullWidth
                    label="Alias"
                    variant="outlined"
                    name="propAbbrevation"
                    disabled={timeSeriesChildColumnListData.some(
                      (item) =>
                        item.propName === field.propName && item.propAbbrevation === field.propAbbrevation
                    ) || params.colDef.template}
                    value={field.propAbbrevation}
                    onChange={(event) => handleInputChange(index, event)}
                    onKeyDown={(event) => handleKeyDown(event, index, 1)}
                    inputRef={(el) =>
                      (inputRefs.current[index * 7 + 1] = el)
                    }
                  />
                </Grid>
                <Grid item xs={isTimeSeries ? 3 : 2}>
                  <select
                    className="MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-formControl MuiInputBase-sizeSmall"
                    name="propType"
                    value={field.propType}
                    style={{
                      width: "100%",
                      marginLeft: "6px",
                      padding: "8.5px 10px",
                      label:"Type",
                      border: "0.5px solid rgba(0, 0, 0, 0.38)",
                      color:
                        field.propType === "none" || timeSeriesChildColumnListData.some(
                          (item) =>
                            item.propName === field.propName && item.propAbbrevation === field.propAbbrevation
                        ) || params.colDef.template
                          ? "rgba(0, 0, 0, 0.63)"
                          : "black",
                      fontSize: "12px",
                      borderRadius: "20px",
                    }}
                    disabled={
                      timeSeriesChildColumnListData.some(
                        (item) =>
                          item.propName === field.propName &&
                          item.propAbbrevation === field.propAbbrevation
                      ) || Boolean(params.colDef.template)
                    }

                    onChange={(e) => handleInputChange(index, e)}
                    onFocus={(e) =>
                      (e.target.style.border = "2px solid rgb(25, 118, 210)")
                    }
                    onBlur={(e) =>
                      (e.target.style.border = "0.5px solid rgb(186, 191, 199)")
                    }
                    onMouseOver={(e) =>
                     { if(!e.target.disabled){
                        (e.target.style.border = "0.5px solid black")
                      }}
                    }
                    onMouseOut={(e) =>
                     { if(!e.target.disabled){
                        (e.target.style.border = "0.5px solid rgb(186, 191, 199)")
                      }}
                    }
                    onKeyDown={(event) => handleKeyDown(event, index, 2)}
                    ref={(el) => (inputRefs.current[index * 7 + 2] = el)}
                  >
                    <option value="none" disabled hidden>
                      Select Data Type
                    </option>
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="date">Date</option>
                    <option value="multi-list">Multi Select List</option>
                    <option value="single-list">Single Select List</option>
                  </select>
                </Grid>
                {!isTimeSeries && (
                  <Grid item xs={2} style={{ display: "flex", flexDirection: "row" }}>
                    <select
                    className="MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-formControl MuiInputBase-sizeSmall"
                    name="rollup"
                    value={field.rollup || 'None'}
                    style={{
                      width: "100%",
                      marginLeft: "6px",
                      padding: "8.5px 10px",
                      label:"Type",
                      border: "0.5px solid rgba(0, 0, 0, 0.38)",
                      color:
                        field.propType === "none"  || params.colDef.template
                          ? "rgba(0, 0, 0, 0.63)"
                          : "black",
                      fontSize: "12px",
                      borderRadius: "20px",
                    }}
                    disabled = {!isRollupApplicable(field.propType) || params.colDef.template}
                    onChange={(e) => handleInputChange(index, e)}
                    onFocus={(e) =>
                      (e.target.style.border = "2px solid rgb(25, 118, 210)")
                    }
                    onBlur={(e) =>
                      (e.target.style.border = "0.5px solid rgb(186, 191, 199)")
                    }
                    onMouseOver={(e) =>
                     { if(!e.target.disabled){
                        (e.target.style.border = "0.5px solid black")
                      }}
                    }
                    onMouseOut={(e) =>
                     { if(!e.target.disabled){
                        (e.target.style.border = "0.5px solid rgb(186, 191, 199)")
                      }}
                    }
                    onKeyDown={(event) => handleKeyDown(event, index, 3)}
                    ref={(el) => (inputRefs.current[index * 7 + 3] = el)}
                  >
                    <option value="None" >
                    None
                    </option>
                    <option value='Min'>Min</option>
                    <option value='Max'>Max</option>
                    {field.propType === 'number' && <option  value='Avg'>Avg</option>}
                    {field.propType === 'number' && <option value='Sum'>Sum</option>}
                  </select>
                    {/* <RollupMenu
                      field={field}
                      localUpdatedDataType={field.propType}
                      rollup={field.rollup}
                      ref={inputRefs}
                      onChange={(type) => handleInputChange(index, { target: { name: "rollup", value: type } })}
                    /> */}
                  </Grid>
                )}
                <Grid
                  item
                  xs={2}
                  sx={{
                    pl: 1,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 1, // adds spacing between the two boxes
                  }}
                >
                  <Box sx={{ width: 40 }}>
                    <SelectColorBox
                      type="bgColor"
                      color={field.propBgColor}
                      onChange={(color) =>
                        handleInputChange(index, {
                          target: { name: "propBgColor", value: color },
                        })
                      }
                      onKeyDown={(event) => handleKeyDown(event, index, 4)}
                      disable={timeSeriesChildColumnListData.some(
                        (item) =>
                          item.propName === field.propName &&
                          item.propAbbrevation === field.propAbbrevation
                      ) || params.colDef.template}
                      ref={(el) => (bgColorRefs.current[index * 7 + 4] = el)}
                    />
                  </Box>

                  <Box sx={{ width: 40 }}>
                    <SelectColorBox
                      type="fgColor"
                      color={field.propFgColor}
                      onChange={(color) =>
                        handleInputChange(index, {
                          target: { name: "propFgColor", value: color },
                        })
                      }
                      disable={timeSeriesChildColumnListData.some(
                        (item) =>
                          item.propName === field.propName &&
                          item.propAbbrevation === field.propAbbrevation
                      ) || params.colDef.template}
                      onKeyDown={(event) => handleKeyDown(event, index, 5)}
                      ref={(el) => (fgColorRefs.current[index * 7 + 5] = el)}
                    />
                  </Box>
                </Grid>

                <Grid item xs={2} sx={{ pl: 1 }}>
                  {fields.length > 1 && (
                    <IconButton
                      onClick={() => handleRemoveField(index)}
                      sx={{ p: 0.5 }}
                      onKeyDown={(event) => handleKeyDown(event, index, 6)}
                      ref={(el) => (removeButtonRefs.current[index * 7 + 6] = el)}
                      disabled={timeSeriesChildColumnListData.some(
                        (item) =>
                          item.propName === field.propName && item.propAbbrevation === field.propAbbrevation
                      ) ||params.colDef.template }
                      >
                      <RemoveCircleOutline />
                    </IconButton>
                  )}
                  {index === fields.length - 1 &&
                    field.propName &&
                    field.propAbbrevation &&
                    field.propType && ((field.propType === 'multi-list' || field.propType === 'single-list') ? field.propList : true) && (
                      <IconButton onClick={handleAddField} sx={{ p: 0.5 }} 
                      onKeyDown={handleAddCircleButtonKeyDown}
                      disabled = {params.colDef.template}
                      ref={(el) => (addCircleButtonRefs.current[index * 7 + 7] = el)}
                      >
                        <AddCircleRounded />
                      </IconButton>
                    )}
                </Grid>
                {(field.propType === "single-list" || field.propType === "multi-list") && (
                <Grid item xs={12}>
                  <ListStructure
                    propListRefs={(el) => (propListRefs.current[index * 7 + 8] = el)}
                    handleKeyDownOnPropList={handleKeyDownOnPropList}
                    setListValue={setListValue}
                    listValue={listValue}
                    value={field?.propList}
                    index={index}
                    handleInputChange={handleInputChange}
                    disable = {params.colDef.template}
                  />
                </Grid>
              )}
              </Grid>
            ))}
          </Box>
          <Box
            sx={{
              padding: "15px 24px",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.5rem",
            }}
          >
            <Button
              style={{
                border: "1px solid #134157",
                background: "transparent",
                color: params.colDef.template?"grey" :"#134157",
                fontWeight: "bold",
                borderRadius: "25px",
                fontSize: "12px",
                padding: "3px 4px",
              }}
              disabled = {params.colDef.template}
              onClick={(e) => handleAddValue(e, "ok")}
              ref={okButtonRefs}
              onKeyDown={handleOkButtonKeyDown}
            >
              OK
            </Button>
            <Button
              style={{
                border: "1px solid #d32f2f",
                background: "transparent",
                color: "#d32f2f",
                fontWeight: "bold",
                borderRadius: "25px",
                fontSize: "12px",
                padding: "3px 4px",
              }}
              onClick={handlePopoverClose}
              ref={cancelButtonRefs}
              onKeyDown={handleCancelButtonKeyDown}
            >
              CANCEL
            </Button>
          </Box>{" "}
        </Box>
      </Popover>
    </Box>

  
  );
});

MultiDimensionalValueTypeEditor.propTypes = {
  params: PropTypes.object.isRequired,
  isEditRowPopup: PropTypes.bool,
  prevRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  gridRef: PropTypes.shape({
    current: PropTypes.shape({
      api: PropTypes.shape({
        getColumnDefs: PropTypes.func,
        applyTransaction: PropTypes.func,
      }),
    }),
  }),
}


export default MultiDimensionalValueTypeEditor;

